import type { SetupChecklistItem, SetupChecklistState } from "@unbox/api";
import { XClose } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { cx } from "@/utils/cx";
import { SETUP_GROUPS, type SetupItemDefinition, setupItemsByGroup } from "../lib/setup-items";
import { progressPercent } from "../lib/setup-progress";
import { SetupChecklistRow } from "./setup-checklist-row";

export type SetupPanelVariant = "dark" | "light";

type Props = {
  state: SetupChecklistState;
  variant?: SetupPanelVariant;
  currentUserId?: string;
  canComplete: boolean;
  destinationFor: (definition: SetupItemDefinition) => void;
  onComplete: (item: SetupChecklistItem) => void;
  onIgnore: (item: SetupChecklistItem, ignored: boolean) => void;
  onClose: () => void;
};

export function SetupChecklistPanel({
  state,
  variant = "dark",
  currentUserId,
  canComplete,
  destinationFor,
  onComplete,
  onIgnore,
  onClose,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const measure = () => setMoreBelow(list.scrollTop + list.clientHeight < list.scrollHeight - 1);

    measure();
    list.addEventListener("scroll", measure, { passive: true });

    const observer = new ResizeObserver(measure);
    observer.observe(list);
    for (const child of Array.from(list.children)) observer.observe(child);

    return () => {
      list.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [state.items]);

  const dark = variant === "dark";
  const muted = dark ? "text-white/60" : "text-gray-500";
  const byItem = new Map(state.items.map((item) => [item.item, item]));

  return (
    <div
      className={cx(
        "relative flex h-full w-full flex-col gap-5 overflow-hidden px-6 pt-6",
        dark ? "setup-panel-dark bg-[#2e1f3a]" : "bg-unbox-cream-25",
      )}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className={cx(
          "absolute top-4 right-4 inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-150",
          dark
            ? "text-white/60 hover:bg-white/10 hover:text-white"
            : "text-gray-500 hover:bg-gray-100 hover:text-gray-900",
        )}
      >
        <XClose className="size-[18px]" />
      </button>

      <div className="flex flex-col gap-1 pr-10">
        <h2 className={cx("text-lg leading-7 font-semibold", dark ? "text-white" : "text-gray-900")}>Setup Unbox</h2>
        <p className={cx("text-sm", muted)}>
          {state.summary.done} of {state.summary.active} done
        </p>
      </div>

      <div className={cx("h-1.5 w-full overflow-hidden rounded-full", dark ? "bg-white/10" : "bg-gray-200")}>
        <div
          className="h-full rounded-full bg-gradient-to-r from-warning-500 to-[#c0a7f2] transition-[width] duration-300"
          style={{ width: `${progressPercent(state.summary.done, state.summary.active)}%` }}
        />
      </div>

      {state.summary.settled && (
        <div
          className={cx(
            "rounded-xl border px-3.5 py-3",
            dark ? "border-success-500/30 bg-success-500/12" : "border-success-200 bg-success-50",
          )}
        >
          <p className={cx("text-sm font-semibold", dark ? "text-white" : "text-success-800")}>You're all set</p>
          <p className={cx("text-xs", muted)}>Setup is finished. This panel goes away when you close it.</p>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={listRef}
          className={cx(
            "unbox-scroll flex flex-1 flex-col gap-9 overflow-y-auto pb-6",
            dark ? "unbox-scroll-dark" : "unbox-scroll-light",
          )}
        >
          {SETUP_GROUPS.map((group) => (
            <div key={group.id} className="flex flex-col gap-2">
              <p
                className={cx(
                  "px-0.5 text-[11px] leading-4 font-semibold tracking-[0.08em] uppercase",
                  dark ? "text-white/50" : "text-gray-500",
                )}
              >
                {group.eyebrow}
              </p>

              {setupItemsByGroup(group.id).map((definition) => {
                const itemState = byItem.get(definition.item);
                if (!itemState) return null;

                return (
                  <SetupChecklistRow
                    key={definition.item}
                    definition={definition}
                    state={itemState}
                    dark={dark}
                    actorIsMe={!!currentUserId && itemState.completedBy?.userId === currentUserId}
                    canComplete={canComplete}
                    actions={{
                      visit: () => destinationFor(definition),
                      complete: () => onComplete(definition.item),
                      ignore: () => onIgnore(definition.item, true),
                      unignore: () => onIgnore(definition.item, false),
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>

        <div
          aria-hidden
          className={cx(
            "pointer-events-none absolute inset-x-0 bottom-0 h-12 transition-opacity duration-200",
            dark
              ? "bg-[linear-gradient(to_top,#2e1f3a,rgba(46,31,58,0))]"
              : "bg-[linear-gradient(to_top,#fffcf9,rgba(255,252,249,0))]",
          )}
          style={{ opacity: moreBelow ? 1 : 0 }}
        />
      </div>
    </div>
  );
}
